import React from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import {
  WORKFLOW_EMAIL_RECIPIENT_SOURCES,
  WorkflowEmailConfig,
  WorkflowEmailRecipientSource,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";
import WorkflowUserPicker from "./WorkflowUserPicker";

/** The prototype's Email wording, verbatim. */
const EMAIL_DISCLAIMER =
  "Enterprise vs Portal: a manager / supervisor is resolved from Enterprise; “Creator” is the Portal user who started the case.";

interface WorkflowEmailPanelProps {
  config: WorkflowEmailConfig;
  onChange: (config: WorkflowEmailConfig) => void;
}

const WorkflowEmailPanel: React.FC<WorkflowEmailPanelProps> = ({ config, onChange }) => {
  const viewOnly = useWorkflowReadOnly();
  const dynamicRecipient = config.recipientSource !== "specific-user";
  // Specific user is an Enterprise user, picked like Approval and Task (Epic 80430 QA item 14), and
  // saved as their id - the send looks up the address. A step saved earlier with a typed address
  // keeps sending to it until someone picks a user in its place.
  const typedAddress = !dynamicRecipient && config.recipient.includes("@") ? config.recipient : "";

  return (
    <Box className="workflow-canvas-dialog__config-panel">
      <Box className="workflow-canvas-dialog__config-grid">
        <FormControl disabled={viewOnly} size="small" fullWidth>
          <InputLabel id="email-recipient-source-label">Send to</InputLabel>
          <Select
            labelId="email-recipient-source-label"
            label="Send to"
            value={config.recipientSource}
            onChange={(event: SelectChangeEvent) =>
              // An address and a field reference aren't interchangeable, so a switch clears it.
              onChange({
                ...config,
                recipientSource: event.target.value as WorkflowEmailRecipientSource,
                recipient: "",
              })
            }
          >
            {WORKFLOW_EMAIL_RECIPIENT_SOURCES.map((source) => (
              <MenuItem key={source.value} value={source.value}>
                {source.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {dynamicRecipient ? (
          <TextField
            disabled={viewOnly}
            label="Reference"
            size="small"
            value={config.recipient}
            onChange={(event) => onChange({ ...config, recipient: event.target.value })}
            placeholder="Field the person comes from"
          />
        ) : (
          <WorkflowUserPicker
            id="email-recipient-user"
            label="Recipient"
            value={typedAddress ? "" : config.recipient}
            onChange={(recipient) => onChange({ ...config, recipient })}
            disabled={viewOnly}
            placeholder="Pick a user"
            helperText={
              typedAddress
                ? `Current recipient: ${typedAddress} (typed address). Pick a user to replace it.`
                : undefined
            }
          />
        )}
      </Box>

      {dynamicRecipient && (
        <Alert severity="info" className="workflow-canvas-dialog__disclaimer">
          {EMAIL_DISCLAIMER}
        </Alert>
      )}

      <TextField
        disabled={viewOnly}
        label="Subject"
        size="small"
        fullWidth
        value={config.subject}
        onChange={(event) => onChange({ ...config, subject: event.target.value })}
      />
      <TextField
        disabled={viewOnly}
        label="Body"
        size="small"
        fullWidth
        multiline
        minRows={6}
        value={config.body}
        onChange={(event) => onChange({ ...config, body: event.target.value })}
        helperText="Use {{variable}} tokens to insert workflow values."
      />
      <TextField
        disabled={viewOnly}
        label="Attach"
        size="small"
        fullWidth
        value={config.attachment}
        onChange={(event) => onChange({ ...config, attachment: event.target.value })}
        placeholder="e.g. Executed PIF (PDF)"
      />
    </Box>
  );
};

export default WorkflowEmailPanel;
